import type React from "react";
import { Link } from "react-router";

import { WorkThumb } from "../lib/WorkThumb.tsx";
import { SETTLEMENT_COPY } from "./settlements.constants.ts";
import type { SettlementRow as Row } from "./settlements.model.ts";

/**
 * One settlement, with its arithmetic and its control.
 *
 * The panel is a `<dl>` of label/value pairs the model already formatted, which
 * is how every detail table in this app is built — `WorkIdentity` and
 * `ProvenanceEntryRead` both do it, and there is no `<table>` outside
 * `ActivityTable`. Nothing here computes or branches on a domain rule.
 */
export function SettlementRow({
  row,
  action,
}: {
  row: Row;
  /** The row's own control, or nothing when there is nothing to do. */
  action: React.ReactNode;
}) {
  return (
    <li className={`gallery__settlement gallery__settlement--${row.state}`}>
      {/* OUTSIDE the link, matching every other row on this rail. A control
          nested in an anchor is a control that navigates when a click misses it
          by two pixels, and one of these commits a gallery to having been paid. */}
      <Link className="gallery__settlement-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />

        <div className="gallery__settlement-body">
          <p className="gallery__settlement-title">
            {row.title}
            <span className={`gallery__settlement-state gallery__settlement-state--${row.state}`}>
              {row.stateLabel}
            </span>
          </p>
          <p className="gallery__settlement-meta">{row.line}</p>
          <p className="gallery__settlement-code">{row.codeLine}</p>
        </div>

        <div className="gallery__settlement-due">
          <span className="gallery__settlement-duelabel">{SETTLEMENT_COPY.dueLabel}</span>
          <span
            className={`gallery__settlement-dueval${
              row.dueToGallery === SETTLEMENT_COPY.pending ? " gallery__settlement-dueval--pending" : ""
            }`}
          >
            {row.dueToGallery}
          </span>
        </div>
      </Link>

      <div className="gallery__terms">
        <div className="gallery__terms-head">{row.termsLabel}</div>
        <dl className="gallery__terms-figures">
          {row.figures.map((figure) => (
            <div key={figure.label}>
              <dt>{figure.label}</dt>
              <dd
                className={[
                  figure.negative ? "is-negative" : "",
                  figure.pending ? "is-pending" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {figure.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="gallery__settlement-foot">
        <p className="gallery__settlement-note">{row.note}</p>
        {action}
      </div>
    </li>
  );
}
